import type { ScheduleResult, Stop, StopSchedule } from "./types.js";

const minute = 60_000;
const toMs = (value?: string | null) => value ? new Date(value).getTime() : null;
const iso = (value: number | null) => value == null || !Number.isFinite(value) ? null : new Date(value).toISOString();

function activeStops(stops: Stop[]) {
  return [...stops]
    .sort((a, b) => a.order - b.order)
    .filter((stop) => stop.state !== "skipped" && (stop.kind !== "suggested" || stop.accepted));
}

function backwardDeadlines(stops: Stop[]) {
  const latestDepartures = new Map<string, number | null>();
  let latestArrivalForNext: number | null = null;

  for (let index = stops.length - 1; index >= 0; index -= 1) {
    const stop = stops[index];
    const ownDeadline = stop.kind === "required" ? toMs(stop.windowEnd) : null;
    let latestDeparture: number | null = null;

    if (index < stops.length - 1 && latestArrivalForNext != null) {
      latestDeparture = latestArrivalForNext - stop.travelMinutesToNext * minute;
    }

    latestDepartures.set(stop.id, latestDeparture);

    const latestFromFuture: number | null = latestDeparture == null
      ? null
      : latestDeparture - Math.max(0, stop.minDurationMinutes) * minute;

    if (ownDeadline != null && latestFromFuture != null) latestArrivalForNext = Math.min(ownDeadline, latestFromFuture);
    else latestArrivalForNext = ownDeadline ?? latestFromFuture;
  }

  return latestDepartures;
}

export function calculateSchedule(allStops: Stop[], nowInput: Date | string = new Date()): ScheduleResult {
  const now = typeof nowInput === "string" ? new Date(nowInput).getTime() : nowInput.getTime();
  const stops = activeStops(allStops);
  const deadlines = backwardDeadlines(stops);
  const items: StopSchedule[] = [];
  const warnings: string[] = [];
  let cursor: number | null = stops.length ? (toMs(stops[0].targetArrival) ?? now) : null;
  let currentStopId: string | null = null;
  let nextStopId: string | null = null;
  let totalLate = 0;

  for (let index = 0; index < stops.length; index += 1) {
    const stop = stops[index];
    const windowStart = toMs(stop.windowStart);
    const windowEnd = toMs(stop.windowEnd);
    const actualArrival = toMs(stop.checkedInAt);
    const actualDeparture = toMs(stop.departedAt);
    const target = toMs(stop.targetArrival);
    let predictedArrival = actualArrival ?? Math.max(cursor ?? now, target ?? Number.NEGATIVE_INFINITY);
    let waitMinutes = 0;

    let activityStart = predictedArrival;
    if (windowStart != null && predictedArrival < windowStart) {
      waitMinutes = Math.ceil((windowStart - predictedArrival) / minute);
      activityStart = windowStart;
    }

    const predictedDeparture = actualDeparture ?? (
      stop.state === "checked_in"
        ? Math.max(now, activityStart + stop.plannedDurationMinutes * minute)
        : activityStart + stop.plannedDurationMinutes * minute
    );

    let lateMinutes = 0;
    let recommendation: StopSchedule["recommendation"] = "on_time";
    let message = "En horario";

    if (windowEnd != null && predictedArrival > windowEnd) {
      lateMinutes = Math.ceil((predictedArrival - windowEnd) / minute);
      totalLate = Math.max(totalLate, lateMinutes);
      if (stop.kind === "required") {
        recommendation = "late";
        message = `Llegada ${lateMinutes} min después del límite`;
        warnings.push(`${stop.name}: llegada tardía estimada`);
      } else {
        recommendation = "skip_recommended";
        message = `Conviene omitirla: llegarían ${lateMinutes} min tarde`;
      }
    }

    const latestDeparture = deadlines.get(stop.id) ?? null;
    const maxStayRemaining = latestDeparture == null ? null : Math.floor((latestDeparture - now) / minute);
    if (latestDeparture != null && predictedDeparture > latestDeparture && recommendation === "on_time") {
      const over = Math.ceil((predictedDeparture - latestDeparture) / minute);
      if (stop.kind === "required") {
        recommendation = "late";
        message = `Esta parada retrasa un compromiso posterior ${over} min`;
        totalLate = Math.max(totalLate, over);
        warnings.push(`${stop.name}: el tramo obligatorio entra en conflicto`);
      } else {
        recommendation = "skip_recommended";
        message = `Conviene omitirla para recuperar ${over} min`;
      }
    }
    if (stop.state === "checked_in" && maxStayRemaining != null && recommendation === "on_time") {
      if (maxStayRemaining <= 0) {
        recommendation = "leave_now";
        message = maxStayRemaining < 0 ? `Salgan ahora · atraso de ${Math.abs(maxStayRemaining)} min` : "Es momento de salir";
      } else if (maxStayRemaining <= 15) {
        recommendation = "leave_soon";
        message = `Salgan antes de ${formatTime(latestDeparture!, stop.timezone)}`;
      } else {
        message = `Pueden quedarse hasta ${formatTime(latestDeparture!, stop.timezone)}`;
      }
    }

    if (stop.state === "checked_in" && !actualDeparture) currentStopId = stop.id;
    if (!nextStopId && stop.state === "pending") nextStopId = stop.id;

    items.push({
      stopId: stop.id,
      predictedArrival: iso(predictedArrival),
      predictedDeparture: iso(predictedDeparture),
      latestDeparture: iso(latestDeparture),
      waitMinutes,
      lateMinutes,
      maxStayRemainingMinutes: maxStayRemaining,
      recommendation,
      message
    });

    cursor = predictedDeparture + stop.travelMinutesToNext * minute;
  }

  if (!currentStopId) currentStopId = stops.find((stop) => stop.state === "pending")?.id ?? null;
  return {
    generatedAt: new Date(now).toISOString(),
    feasible: !items.some((item) => item.recommendation === "late"),
    minutesLate: totalLate,
    nextStopId,
    currentStopId,
    items,
    warnings
  };
}

export function formatTime(value: number | string | Date, timezone = "America/Los_Angeles") {
  const date = typeof value === "number" || typeof value === "string" ? new Date(value) : value;
  return new Intl.DateTimeFormat("es-MX", { hour: "numeric", minute: "2-digit", timeZone: timezone }).format(date);
}

export function stopSchedule(result: ScheduleResult, stopId: string) {
  return result.items.find((item) => item.stopId === stopId) ?? null;
}
